// src/components/looker/admin/LookerTemplateTable.tsx
/**
 * /admin/looker-templates, part one: every template in the template folder,
 * with how many workspaces have it and what a master can do to it. Editing and
 * the delete confirmation happen in the row; nothing here calls a service —
 * the page owns the writes and refetches after each.
 *
 * @module components/looker/admin/LookerTemplateTable
 */
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { LookerTemplateSummary } from '@/services/looker/lookerTypes';
import { TemplateActionsMenu } from './TemplateActionsMenu';

interface Props {
  templates: LookerTemplateSummary[];
  /** Workspaces granted each template, by id (the default is everyone's). */
  grantCounts: ReadonlyMap<string, number>;
  busy: boolean;
  /** `description` is undefined when it was not changed, so Looker's stays as it is. */
  onRename: (id: string, title: string, description: string | undefined) => Promise<boolean>;
  onCopy: (id: string) => void;
  /** Opens the tile builder for a template made in the app. */
  onEditTiles: (id: string) => void;
  onDelete: (id: string) => Promise<boolean>;
  onMakeDefault: (id: string) => void;
}

const BUTTON =
  'inline-flex h-7 items-center rounded-md border border-neutral-300 bg-white px-2 text-xs font-medium text-ink-900 hover:bg-neutral-100 disabled:cursor-not-allowed disabled:opacity-50';
const DANGER =
  'inline-flex h-7 items-center rounded-md border border-red-300 bg-white px-2 text-xs font-medium text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50';
const PRIMARY =
  'inline-flex h-7 items-center rounded-md bg-primary-600 px-2 text-xs font-medium text-ink-950 hover:bg-primary-700 disabled:opacity-50';
const INPUT = 'w-full rounded-md border border-neutral-300 px-2 py-1 text-sm';

/** How long "Link copied" / "Copy failed" stays on the button, ms. */
const COPY_FEEDBACK_MS = 2000;

/**
 * The Looker link, for pasting into the browser that holds the Looker login.
 * navigator.clipboard exists only in a secure context, and UAT is served over
 * plain http on an IP, so the old select-and-copy path is the fallback there.
 */
const copyText = async (text: string): Promise<boolean> => {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
    const area = document.createElement('textarea');
    area.value = text;
    area.setAttribute('readonly', '');
    area.style.position = 'fixed';
    area.style.opacity = '0';
    document.body.appendChild(area);
    area.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(area);
    return ok;
  } catch {
    return false;
  }
};

interface Draft {
  id: string;
  title: string;
  description: string;
}

export const LookerTemplateTable: React.FC<Props> = ({ templates, grantCounts, busy, onRename, onCopy, onEditTiles, onDelete, onMakeDefault }) => {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<Draft | null>(null);
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const [copied, setCopied] = useState<{ id: string; ok: boolean } | null>(null);
  const copyTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => {
    if (copyTimer.current) clearTimeout(copyTimer.current);
  }, []);

  const copyLink = async (id: string, url: string) => {
    const ok = await copyText(url);
    setCopied({ id, ok });
    if (copyTimer.current) clearTimeout(copyTimer.current);
    copyTimer.current = setTimeout(() => setCopied(null), COPY_FEEDBACK_MS);
  };
  const copyLabel = (id: string): string => {
    if (copied?.id !== id) return t('looker.admin.copyLink');
    return copied.ok ? t('looker.admin.linkCopied') : t('looker.admin.copyLinkFailed');
  };

  const save = async () => {
    if (!draft || !draft.title.trim()) return;
    const before = templates.find((tp) => tp.id === draft.id)?.description ?? '';
    const description = draft.description === before ? undefined : draft.description;
    if (await onRename(draft.id, draft.title.trim(), description)) setDraft(null);
  };
  const confirmDelete = async (id: string) => {
    if (await onDelete(id)) setConfirmingId(null);
  };

  if (templates.length === 0) return <p className="px-4 py-6 text-sm text-neutral-800">{t('looker.admin.empty')}</p>;

  return (
    <table className="w-full text-sm">
      <thead className="bg-neutral-50 text-left text-neutral-700">
        <tr>
          <th className="px-4 py-3 font-medium">{t('looker.admin.colTitle')}</th>
          <th className="px-4 py-3 font-medium">{t('looker.admin.colDescription')}</th>
          <th className="px-4 py-3 font-medium">{t('looker.admin.colWorkspaces')}</th>
          <th className="px-4 py-3 font-medium sr-only">{t('looker.admin.colActions')}</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-neutral-100">
        {templates.map((tp) => {
          const editing = draft?.id === tp.id ? draft : null;
          const grants = grantCounts.get(tp.id) ?? 0;
          return (
            <tr key={tp.id} className="align-top">
              <td className="px-4 py-3 text-ink-900">
                {editing ? (
                  <input
                    className={INPUT}
                    aria-label={t('looker.admin.titleLabel')}
                    value={editing.title}
                    maxLength={100}
                    disabled={busy}
                    onChange={(e) => setDraft({ ...editing, title: e.target.value })}
                  />
                ) : (
                  <span className="inline-flex flex-wrap items-center gap-2">
                    <span className="font-medium">{tp.title}</span>
                    {tp.isDefault && (
                      <span className="rounded-full bg-primary-500 px-2 py-0.5 text-xs font-semibold text-ink-950">{t('looker.admin.default')}</span>
                    )}
                    {/* Where it was made: only an app-made template opens in the tile builder. */}
                    <span
                      className="rounded-full border border-neutral-300 px-2 py-0.5 text-xs font-medium text-neutral-800"
                      title={tp.appBuilt ? t('looker.admin.sourceAppHint') : t('looker.admin.sourceLookerHint')}
                    >
                      {tp.appBuilt ? t('looker.admin.sourceApp') : t('looker.admin.sourceLooker')}
                    </span>
                  </span>
                )}
              </td>
              <td className="px-4 py-3 text-neutral-800">
                {editing ? (
                  <textarea
                    className={INPUT}
                    aria-label={t('looker.admin.descriptionLabel')}
                    value={editing.description}
                    maxLength={500}
                    rows={2}
                    disabled={busy}
                    onChange={(e) => setDraft({ ...editing, description: e.target.value })}
                  />
                ) : (
                  tp.description ?? '—'
                )}
              </td>
              <td className="px-4 py-3 text-neutral-800">{tp.isDefault ? t('looker.admin.everyWorkspace') : grants}</td>
              <td className="px-4 py-3 text-right">
                {editing ? (
                  <div className="inline-flex gap-2">
                    <button type="button" className={PRIMARY} disabled={busy || !editing.title.trim()} onClick={() => void save()}>
                      {t('looker.admin.save')}
                    </button>
                    <button type="button" className={BUTTON} onClick={() => setDraft(null)}>{t('looker.admin.cancel')}</button>
                  </div>
                ) : confirmingId === tp.id ? (
                  <div className="ml-auto flex max-w-sm flex-col items-end gap-2 text-left">
                    <p className="text-xs text-neutral-800">{t('looker.admin.confirmDelete', { title: tp.title, n: grants })}</p>
                    <div className="inline-flex gap-2">
                      <button type="button" className={DANGER} disabled={busy} onClick={() => void confirmDelete(tp.id)}>
                        {t('looker.admin.confirmDeleteButton')}
                      </button>
                      <button type="button" className={BUTTON} onClick={() => setConfirmingId(null)}>{t('looker.admin.cancel')}</button>
                    </div>
                  </div>
                ) : (
                  <TemplateActionsMenu
                    template={tp}
                    busy={busy}
                    copyLinkLabel={copyLabel(tp.id)}
                    onEdit={() => setDraft({ id: tp.id, title: tp.title, description: tp.description ?? '' })}
                    onCopy={() => onCopy(tp.id)}
                    onEditTiles={() => onEditTiles(tp.id)}
                    onMakeDefault={() => onMakeDefault(tp.id)}
                    onCopyLink={() => void copyLink(tp.id, tp.lookerUrl as string)}
                    onDelete={() => setConfirmingId(tp.id)}
                  />
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};
